import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, Star, X } from "lucide-react";
import { waLink } from "../../api";
import { WhatsAppIcon } from "../Logo";

export function AboutUs() {
  const stats = [
    { value: "+10", label: "Años de experiencia" },
    { value: "+15.000", label: "Pedidos entregados" },
    { value: "4,9/5", label: "Valoración media" },
    { value: "24/48h", label: "Envío en toda España" },
  ];

  return (
    <section id="nosotros" data-testid="about-section" className="bg-[#1E2128]/40 px-4 py-16 lg:px-8">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#2EE6D6]">
            Sobre nosotros
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Más de 10 años junto a los vapeadores
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-[#8A8F98] sm:text-base">
            NubeVape España nació en 2014 como una pequeña tienda de vapeo de barrio. Desde
            entonces hemos crecido con una idea clara: ofrecer producto original, precios justos y
            un trato cercano, de persona a persona.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-[#8A8F98] sm:text-base">
            Hoy trabajamos con los principales distribuidores oficiales, lo que nos permite tener
            los últimos modelos del mercado — Razz Bar, Bang, Happ Bar, Rock Me y muchas más
            marcas — con stock real y precios que ninguna tienda física puede igualar.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-[#8A8F98] sm:text-base">
            Cada pedido lo gestionamos personalmente por WhatsApp: te confirmamos stock, te
            aconsejamos sabores y te mantenemos informado hasta que lo recibes. Sin bots, sin
            esperas y sin sorpresas.
          </p>
          <a
            href={waLink("Hola NubeVape España, quiero conoceros mejor. ¿Me contáis cómo trabajáis?")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-whatsapp-btn"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-black transition-colors hover:bg-[#20BA5A]"
          >
            <WhatsAppIcon className="h-4 w-4" />
            Hablar con nosotros
          </a>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {stats.map((s) => (
            <div
              key={s.label}
              data-testid={`about-stat-${s.label.toLowerCase().replace(/[\s/.]+/g, "-")}`}
              className="rounded-2xl border border-[#2D323E] card-glow p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#2EE6D6]/50"
            >
              <p className="font-heading text-3xl font-extrabold text-gradient sm:text-4xl">
                {s.value}
              </p>
              <p className="mt-2 text-xs font-medium uppercase tracking-widest text-[#8A8F98]">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const REVIEWS = [
  {
    name: "Laura G.",
    city: "Madrid",
    product: "Razz Bar 60K",
    text: "Pedí el lunes por WhatsApp y el miércoles ya lo tenía en casa. El Razz Bar es original, con su código verificado, y el sabor dura hasta la última calada. Repetiré seguro.",
  },
  {
    name: "Adrián M.",
    city: "Valencia",
    product: "Happ Bar GR100K",
    text: "Atención de 10. Me aconsejaron sabores según lo que me gusta y me mandaron foto del stock antes de pagar. Así da gusto comprar.",
  },
  {
    name: "Cristina R.",
    city: "Sevilla",
    product: "Vap Solo Quads 80K",
    text: "Llevo ya tres pedidos con ellos. Precios imposibles de encontrar en tienda física y siempre llega todo bien embalado y rápido.",
  },
  {
    name: "Jorge L.",
    city: "Bilbao",
    product: "Bang 100K",
    text: "Pagué contrareembolso y cero problemas. Se nota que son gente seria. El Bang 100K dura una barbaridad, llevo un mes con él.",
  },
];

function Stars() {
  return (
    <div className="flex gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className="h-4 w-4 fill-[#F59E0B] text-[#F59E0B]" />
      ))}
    </div>
  );
}

export function Testimonials() {
  return (
    <section id="opiniones" data-testid="testimonials-section" className="px-4 py-16 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="text-center">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#7C5CFF]">
            Opiniones reales
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Lo que dicen nuestros clientes
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-[#8A8F98] sm:text-base">
            Valoración media de 4,9 sobre 5 en más de 2.300 pedidos gestionados por WhatsApp.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {REVIEWS.map((r, idx) => (
            <figure
              key={r.name}
              data-testid={`testimonial-card-${idx}`}
              className="flex flex-col rounded-2xl border border-[#2D323E] bg-[#1E2128] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#7C5CFF]/50"
            >
              <Quote className="mb-3 h-5 w-5 text-[#2EE6D6]" />
              <blockquote className="flex-1 text-sm leading-relaxed text-[#F2F4F6]">
                “{r.text}”
              </blockquote>
              <figcaption className="mt-5 border-t border-[#2D323E] pt-4">
                <Stars />
                <p className="mt-2 text-sm font-semibold">{r.name}</p>
                <p className="text-xs text-[#8A8F98]">
                  {r.city} · Compró {r.product}
                </p>
                <p className="mt-1 text-[11px] font-medium text-[#2EE6D6]">Compra verificada</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

const BULK_TIERS = [
  {
    range: "1 – 9 uds.",
    title: "Precio normal",
    text: "Elige cualquier sabor del catálogo y pide unidades sueltas.",
    badge: "0%",
  },
  {
    range: "10 – 49 uds.",
    title: "-25% en el total",
    text: "Caja de 10 o más con sabores mezclados sin coste extra.",
    badge: "-25%",
    highlight: true,
  },
  {
    range: "50+ uds.",
    title: "-50% en el total",
    text: "Precio de mayorista: perfecto para grupos o para revender.",
    badge: "-50%",
    highlight: true,
  },
];

export function BulkDiscounts() {
  return (
    <section id="descuentos" data-testid="bulk-discounts-section" className="px-4 pb-4 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="rounded-3xl border border-[#2D323E] card-glow p-8 lg:p-10">
          <div className="text-center">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#2EE6D6]">
              Cuanto más pides, más ahorras
            </p>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Descuentos por cantidad
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-[#8A8F98] sm:text-base">
              El descuento se aplica automáticamente en el carrito y en el pedido de WhatsApp
              según las unidades de cada producto.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {BULK_TIERS.map((t) => (
              <div
                key={t.range}
                data-testid={`bulk-tier-${t.range.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`rounded-2xl border p-6 text-center transition-all duration-300 hover:-translate-y-1 ${
                  t.highlight
                    ? "border-[#7C5CFF]/40 bg-[#7C5CFF]/10 hover:border-[#7C5CFF]/70"
                    : "border-[#2D323E] bg-[#16181D] hover:border-[#2EE6D6]/40"
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-[#8A8F98]">
                  {t.range}
                </p>
                <p className="mt-2 font-heading text-3xl font-extrabold text-gradient">{t.badge}</p>
                <p className="mt-1 text-sm font-semibold">{t.title}</p>
                <p className="mt-2 text-xs leading-relaxed text-[#8A8F98]">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const COOKIE_KEY = "nubevape_cookies";

export function CookieBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(COOKIE_KEY)) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  if (!open) return null;

  const choose = (value) => {
    localStorage.setItem(COOKIE_KEY, value);
    setOpen(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[90] border-t border-[#2D323E] bg-[#1E2128]/95 px-4 py-4 backdrop-blur-xl animate-fade-up"
    >
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-3xl text-xs leading-relaxed text-[#8A8F98] sm:text-sm">
          Utilizamos <span className="font-semibold text-[#F2F4F6]">cookies propias y de terceros</span>{" "}
          para el funcionamiento de la web, recordar tus preferencias y analizar el tráfico de
          forma anónima. Puedes aceptar o rechazar su uso. Más información en nuestra{" "}
          <Link to="/cookies" data-testid="cookie-policy-link" className="text-[#2EE6D6] underline hover:text-[#7C5CFF]">
            política de cookies
          </Link>.
        </p>
        <div className="flex shrink-0 items-center gap-3">
          <button
            data-testid="cookie-reject-btn"
            onClick={() => choose("rejected")}
            className="flex items-center gap-1.5 rounded-full border border-[#2D323E] px-5 py-2.5 text-sm font-semibold text-[#8A8F98] transition-colors hover:border-[#EF4444]/50 hover:text-[#F2F4F6]"
          >
            <X className="h-4 w-4" /> Rechazar
          </button>
          <button
            data-testid="cookie-accept-btn"
            onClick={() => choose("accepted")}
            className="rounded-full bg-gradient-to-r from-[#2EE6D6] to-[#7C5CFF] px-6 py-2.5 text-sm font-bold text-black transition-opacity hover:opacity-90"
          >
            Aceptar cookies
          </button>
        </div>
      </div>
    </div>
  );
}
